import { useState, useEffect } from 'react';
import { loadSportContent, SportContent } from '../content';

// Cache to prevent repeated loads when component remounts or effect re-runs
const contentCache = new Map<string, SportContent>();

export const useSportContent = (sportId: string) => {
  const [content, setContent] = useState<SportContent | null>(() => contentCache.get(sportId) ?? null);
  const [loading, setLoading] = useState(!contentCache.has(sportId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cached = contentCache.get(sportId);
    if (cached) {
      setContent(cached);
      setLoading(false);
      setError(null);
      return;
    }

    const loadContent = async () => {
      try {
        setLoading(true);
        setError(null);

        const sportContent = await loadSportContent(sportId);
        const transformedContent = {
          drills: sportContent.drills || [],
          strategies: sportContent.strategies || [],
          equipment: sportContent.equipment || [],
          fundamentals: sportContent.fundamentals || [],
          shotDiagnosis: sportContent.shotDiagnosis || [],
          assessments: sportContent.assessments || [],
          videoAnalysis: sportContent.videoAnalysis
        } as SportContent;
        contentCache.set(sportId, transformedContent);
        setContent(transformedContent);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load content');
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [sportId]);

  return { content, loading, error };
};